import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import { VOUCHER_ORDERS_PAGE_PATH } from "@/shared/configs/routes.config";
import { Button } from "@/shared/ui/Button";

const INITIAL_COUNTDOWN = 3;
// One second per countdown step, matching the progress bar's 1s CSS transition;
// also the extra second we wait at 0 to let the bar finish emptying before navigating.
const COUNTDOWN_STEP_MS = 1000;

type NavigateToVoucherCampaignsButtonProps = {
  onCloseModal: () => void
  /** When true, runs the countdown + progress bar and navigates automatically */
  autoNavigate: boolean
};

const NavigateToVoucherCampaignsButton = ({ onCloseModal, autoNavigate }: NavigateToVoucherCampaignsButtonProps) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(INITIAL_COUNTDOWN);

  const navigateNow = useCallback(() => {
    navigate(VOUCHER_ORDERS_PAGE_PATH);
    onCloseModal();
  }, [navigate, onCloseModal]);

  // Tick every second, holding at 0 (display counts down 3 → 0)
  useEffect(() => {
    if (!autoNavigate) return undefined;

    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) clearInterval(interval);
        return Math.max(c - 1, 0);
      });
    }, COUNTDOWN_STEP_MS);

    return () => clearInterval(interval);
  }, [autoNavigate]);

  // Let the bar finish emptying for one extra second at 0 before navigating
  useEffect(() => {
    if (!autoNavigate || countdown > 0) return undefined;

    const timer = setTimeout(navigateNow, COUNTDOWN_STEP_MS);
    return () => clearTimeout(timer);
  }, [autoNavigate, countdown, navigateNow]);

  return (
    <Button
      variant="action"
      onClick={navigateNow}
      className="relative overflow-hidden"
    >
      Aller aux titres-cadeaux
      {autoNavigate && ` (${countdown}s)`}
      {autoNavigate && (
        <span
          className="absolute bottom-0 left-0 h-1 bg-primary transition-all duration-1000 ease-linear"
          style={{ width: `${(countdown / INITIAL_COUNTDOWN) * 100}%` }}
        />
      )}
    </Button>
  );
};

export default NavigateToVoucherCampaignsButton;
